<template>
  <v-container
    fluid
    class="advertiser-page"
    :class="
      $vuetify.breakpoint.mdAndUp
        ? 'py-5 px-10'
        : '' && $vuetify.breakpoint.smAndUp
        ? 'py-2 px-2'
        : ''
    "
  >
    <pageHeader
      title="Create Your Promotional Campaign"
      subtitle="Promote your business for free on LinkBook"
    />

    <!-- Hero Section -->
    <v-row justify="center" class="my-8">
      <v-col cols="12" md="10" lg="8">
        <v-card
          class="hero-card pa-8"
          elevation="8"
          :style="{
            background: 'linear-gradient(135deg, #1976d2 0%, #673ab7 100%)'
          }"
        >
          <v-row align="center">
            <v-col cols="12" md="7">
              <h2 class="text-h4 font-weight-bold white--text mb-4">
                Ready to Promote Your Business?
              </h2>
              <p class="text-h6 white--text mb-6">
                Create your free promotional campaign and reach thousands of
                users. Build your following, increase bookmarks, and grow your
                digital presence—all at zero cost.
              </p>
              <div class="mb-4">
                <v-chip color="white" text-color="primary" class="mr-2 mb-2">
                  <v-icon left small color="primary">mdi-check</v-icon>
                  100% Free Forever
                </v-chip>
                <v-chip color="white" text-color="primary" class="mr-2 mb-2">
                  <v-icon left small color="primary">mdi-check</v-icon>
                  No Credit Card
                </v-chip>
                <v-chip color="white" text-color="primary" class="mb-2">
                  <v-icon left small color="primary">mdi-check</v-icon>
                  Setup in Minutes
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" md="5" class="text-center">
              <img
                src="/img/promote_landing.png"
                alt="Promote your business"
                class="pulse-icon hero-image"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn
                color="white"
                x-large
                elevation="8"
                class="primary--text font-weight-bold"
                @click="openAdvertModal"
              >
                <v-icon left color="primary">mdi-plus-circle</v-icon>
                Create Your Campaign Now
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <v-row
      v-if="loading && userAdverts.length === 0"
      justify="center"
      class="my-8"
    >
      <v-col cols="12" class="text-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
        <p class="mt-4 text-body-1">Loading your campaigns...</p>
      </v-col>
    </v-row>

    <!-- Empty State -->
    <v-row
      v-if="!loading && userAdverts.length === 0 && isLoggedIn"
      justify="center"
      class="my-8"
    >
      <v-col cols="12" md="8" lg="6" class="text-center">
        <v-icon size="120" color="grey lighten-1" class="mb-4">
          mdi-bullhorn-outline
        </v-icon>
        <h3 class="text-h5 font-weight-bold mb-3">No Campaigns Yet</h3>
        <p class="text-body-1 text--secondary mb-6">
          Start promoting your business today! Create your first campaign and
          reach thousands of users.
        </p>
        <v-btn color="primary" large @click="openAdvertModal">
          <v-icon left>mdi-plus-circle</v-icon>
          Create Your First Campaign
        </v-btn>
      </v-col>
    </v-row>

    <!-- Existing Campaigns Section -->
    <v-row
      v-if="!loading && userAdverts.length > 0"
      justify="center"
      class="my-8"
    >
      <v-col cols="12" md="10" lg="8">
        <h3 class="text-h5 font-weight-bold mb-4">Your Campaigns</h3>
        <v-row>
          <v-col
            v-for="advert in userAdverts"
            :key="advert.doc_id"
            cols="12"
            sm="6"
            md="4"
          >
            <v-card elevation="4" class="campaign-card" height="100%">
              <v-img
                :src="advert.background || '/img/banner/networking.jpg'"
                height="120"
              />
              <v-card-title class="text-subtitle-1">
                {{ advert.title }}
              </v-card-title>
              <v-card-subtitle>
                <v-chip
                  small
                  :color="advert.status ? 'success' : 'warning'"
                  text-color="white"
                  class="mt-2"
                >
                  {{ advert.status ? 'Active' : 'Pending Approval' }}
                </v-chip>
              </v-card-subtitle>
              <v-card-text>
                <div class="d-flex justify-space-between">
                  <div class="text-center">
                    <div class="text-h6 primary--text">
                      {{ advert.followers || 0 }}
                    </div>
                    <div class="text-caption">Followers</div>
                  </div>
                  <div class="text-center">
                    <div class="text-h6 primary--text">
                      {{ advert.views || 0 }}
                    </div>
                    <div class="text-caption">Views</div>
                  </div>
                </div>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  text
                  color="primary"
                  :to="
                    `/${process.env.VUE_APP_PROMO_SLUG || 'promo'}/${
                      advert.slug
                    }`
                  "
                >
                  View
                </v-btn>
                <v-spacer />
                <v-btn text color="primary" @click="editAdvert(advert)">
                  Edit
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- What You Get Section -->
    <v-row justify="center" class="my-8">
      <v-col cols="12" md="10" lg="8">
        <h3 class="text-h5 font-weight-bold mb-6 text-center">
          What's Included in Your Free Campaign
        </h3>
        <v-row>
          <v-col cols="12" md="6" lg="4" class="mb-4">
            <v-card class="feature-card pa-6" height="100%" outlined>
              <v-icon size="48" color="primary" class="mb-3">
                mdi-account-multiple
              </v-icon>
              <h4 class="text-h6 font-weight-bold mb-2">Build Your Audience</h4>
              <p class="text-body-2">
                Connect with engaged users and gain followers who will receive
                updates about your latest offerings and news.
              </p>
            </v-card>
          </v-col>
          <v-col cols="12" md="6" lg="4" class="mb-4">
            <v-card class="feature-card pa-6" height="100%" outlined>
              <v-icon size="48" color="success" class="mb-3">
                mdi-bookmark-multiple
              </v-icon>
              <h4 class="text-h6 font-weight-bold mb-2">
                Increase Bookmarks
              </h4>
              <p class="text-body-2">
                Get more customers to save your business. Each bookmark
                represents a potential customer who has shown interest.
              </p>
            </v-card>
          </v-col>
          <v-col cols="12" md="6" lg="4" class="mb-4">
            <v-card class="feature-card pa-6" height="100%" outlined>
              <v-icon size="48" color="purple" class="mb-3">
                mdi-chart-line
              </v-icon>
              <h4 class="text-h6 font-weight-bold mb-2">
                Boost Digital Presence
              </h4>
              <p class="text-body-2">
                Establish your brand presence on a growing platform with
                visibility to our entire user base.
              </p>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- CTA Section -->
    <v-row justify="center" class="my-12">
      <v-col cols="12" md="8" lg="6" class="text-center">
        <v-btn
          color="primary"
          x-large
          elevation="8"
          @click="openAdvertModal"
          class="mb-4"
        >
          <v-icon left>mdi-rocket-launch</v-icon>
          Get Started Now
        </v-btn>
        <p class="text-body-2 text--secondary mt-4">
          Questions? Check out our
          <a href="/promote" class="primary--text">promotional page</a> for more
          details.
        </p>
      </v-col>
    </v-row>

    <!-- Floating Action Button -->
    <v-btn
      color="primary"
      fab
      fixed
      bottom
      right
      x-large
      @click="openAdvertModal"
      elevation="8"
      class="create-fab"
    >
      <v-icon>mdi-plus</v-icon>
    </v-btn>

    <!-- Advert Modal Dialog -->
    <v-dialog v-model="showModal" max-width="900" persistent scrollable>
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="handleModalClose">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            {{ editMode ? 'Edit Campaign' : 'Create Your Campaign' }}
          </v-toolbar-title>
        </v-toolbar>
        <AdvertModal
          :modal="showModal"
          :editAdvert="editMode"
          @modalClose="handleModalClose"
        />
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import db from '../../firebase/init'
import { mapGetters } from 'vuex'
import AdvertModal from '@/components/dialogs/AdvertModal.vue'
import pageHeader from '@/components/layout/PageHeader'

export default {
  name: 'Advertiser',
  metaInfo: {
    title: 'Create Your Promotional Campaign',
    titleTemplate: '%s | LinkBook.io',
    meta: [
      {
        name: 'description',
        content:
          'Create your free promotional campaign on LinkBook. Reach thousands of users, build your following, and grow your digital presence at zero cost.'
      }
    ]
  },
  components: {
    AdvertModal,
    pageHeader
  },
  data() {
    return {
      showModal: false,
      editMode: false,
      userAdverts: [],
      loading: false
    }
  },
  computed: {
    ...mapGetters(['isLoggedIn', 'Uid'])
  },
  async created() {
    if (this.isLoggedIn && this.Uid) {
      await this.fetchUserAdverts()
    }
  },
  methods: {
    openAdvertModal() {
      this.editMode = false
      this.showModal = true
    },
    editAdvert(_advert) {
      this.editMode = true
      this.showModal = true
    },
    handleModalClose(data) {
      this.showModal = false
      if (data && data.success) {
        this.$notify({
          type: 'success',
          group: 'action',
          title: 'Success',
          text: 'Your campaign has been submitted!'
        })
        // Refresh adverts list
        this.fetchUserAdverts()
      }
    },
    async fetchUserAdverts() {
      try {
        this.loading = true
        const advertsRef = db
          .collection('advertisers')
          .doc('starter')
          .collection('adverts')

        const snapshot = await advertsRef
          .where('user_id', '==', this.Uid)
          .orderBy('created', 'desc')
          .get()

        this.userAdverts = []
        snapshot.forEach(doc => {
          this.userAdverts.push({
            doc_id: doc.id,
            ...doc.data()
          })
        })
      } catch (error) {
        console.error('Error fetching adverts:', error)
        // Show user-friendly error notification
        this.$notify({
          type: 'error',
          group: 'action',
          title: 'Error Loading Campaigns',
          text: 'Unable to load your campaigns. Please try again.'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.advertiser-page {
  min-height: 100vh;
}

.hero-card {
  border-radius: 24px;
  overflow: hidden;
}

.pulse-icon {
  animation: pulse 3s ease-in-out infinite;
}

.hero-image {
  max-width: 150px;
  width: 100%;
  height: auto;
  object-fit: contain;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.9;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
}

.campaign-card {
  transition: all 0.3s ease;
  border-radius: 12px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
}

.feature-card {
  transition: all 0.3s ease;
  border-radius: 16px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
}

.create-fab {
  margin-bottom: 20px;
  margin-right: 20px;

  &:hover {
    transform: scale(1.1);
  }
}
</style>
